import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Phone, ShieldCheck, Clock, CurrencyDollar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { COMPANY } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Hero = () => {
  const [form, setForm] = useState({ address: "", name: "", phone: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please enter your name, phone, and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Request received! We'll call you within 24 hours with your cash offer.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us directly and we'll help right away.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="bg-stone-50 pt-32 pb-20 md:pt-40 md:pb-28 px-6 md:px-12 lg:px-16"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7"
        >
          <span className="uppercase text-xs md:text-sm tracking-[0.2em] font-semibold text-emerald-700">
            Trusted Local Home Buyers
          </span>
          <h1 className="font-heading mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            Sell Your House Fast. <br />
            <span className="text-emerald-600">All Cash, No Hassle.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
            Facing foreclosure, behind on payments, or just need to sell fast? We buy houses
            in any condition. Skip the repairs, fees, and months of waiting — close on your timeline.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a
              href={COMPANY.phoneHref}
              data-testid="hero-phone-cta"
              className="inline-flex items-center gap-3 bg-slate-900 text-white rounded-lg px-7 py-4 font-semibold hover:bg-slate-800 transition-all hover:scale-[1.02]"
            >
              <Phone weight="fill" className="h-5 w-5" />
              Call {COMPANY.phoneDisplay}
            </a>
            <span className="text-sm text-slate-500">Free offer • No obligation • Available 24/7</span>
          </div>

          <div className="mt-10 flex flex-wrap gap-6">
            {[
              { icon: CurrencyDollar, text: "Fair cash offer" },
              { icon: Clock, text: "Close in 7 days" },
              { icon: ShieldCheck, text: "Zero fees" },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-slate-700 font-medium">
                <b.icon weight="duotone" className="h-6 w-6 text-emerald-600" />
                {b.text}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="lg:col-span-5"
        >
          <div
            data-testid="hero-lead-form-card"
            className="bg-white p-8 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-stone-100"
          >
            {submitted ? (
              <div data-testid="lead-form-success" className="text-center py-8">
                <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <ShieldCheck weight="fill" className="h-8 w-8 text-emerald-600" />
                </div>
                <h3 className="font-heading mt-6 text-2xl font-semibold text-slate-900">Thank you!</h3>
                <p className="mt-3 text-slate-600">
                  We received your request. Expect a call within 24 hours with your no-obligation cash offer.
                </p>
                <a
                  href={COMPANY.phoneHref}
                  className="mt-6 inline-flex items-center gap-2 text-emerald-700 font-bold text-lg"
                >
                  <Phone weight="fill" className="h-5 w-5" />
                  Or call us now
                </a>
              </div>
            ) : (
              <>
                <h3 className="font-heading text-2xl font-semibold text-slate-900">Get My Cash Offer</h3>
                <p className="mt-2 text-sm text-slate-500">
                  Fill this out and we'll get you a fair offer fast.
                </p>
                <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="hero-lead-form">
                  <input
                    data-testid="input-address"
                    value={form.address}
                    onChange={update("address")}
                    placeholder="Property Address"
                    className="w-full bg-white border border-stone-300 rounded-lg px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none"
                  />
                  <input
                    data-testid="input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your Name"
                    className="w-full bg-white border border-stone-300 rounded-lg px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none"
                  />
                  <input
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="Phone Number"
                    type="tel"
                    className="w-full bg-white border border-stone-300 rounded-lg px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none"
                  />
                  <input
                    data-testid="input-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="Email Address"
                    type="email"
                    className="w-full bg-white border border-stone-300 rounded-lg px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none"
                  />
                  <textarea
                    data-testid="input-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us a bit about your situation (optional)"
                    rows={3}
                    className="w-full bg-white border border-stone-300 rounded-lg px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none resize-none"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="hero-lead-form-submit"
                    className="w-full inline-flex items-center justify-center bg-emerald-600 text-white rounded-lg px-8 py-4 font-semibold hover:bg-emerald-700 transition-all hover:scale-[1.01] shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? "Sending..." : "Get My Cash Offer"}
                  </button>
                  <p className="text-xs text-slate-400 text-center">
                    No spam. No obligation. Your information stays private.
                  </p>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
